/**
 * 密码校验通用工具
 * 供 App.js（首登改密）、pwdModal（管理员重置密码）等场景复用
 */

import Request from "srcDir/util/request";
import { sm4 } from "sm-crypto";
import Storage from "store2";
import config from "projectRoot/config.json";

// 常见弱密码字典
export const WEAK_PASSWORDS = [
  "123456", "12345678", "123456789", "1234567890",
  "111111", "222222", "333333", "444444", "555555",
  "666666", "777777", "888888", "999999", "000000",
  "abcdef", "abc123", "abcd1234", "abc123456",
  "qwerty", "qwerty123", "password", "password123", "passwd",
  "admin", "admin123", "admin1234", "admin888",
  "test123", "test1234",
  "a1b2c3", "aa123456", "woaini", "iloveyou",
  "123abc", "abc456", "1234abcd",
  "p@ssword", "p@ssw0rd", "passw0rd",
  "qweasd", "asd123", "zxc123",
];

/** 检测是否包含连续字符序列（如 abcd、1234、dcba、4321） */
export function isSequential(str) {
  const sequences = "abcdefghijklmnopqrstuvwxyz0123456789";
  const reversed = "9876543210zyxwvutsrqponmlkjihgfedcba";
  for (let i = 0; i <= sequences.length - 4; i++) {
    if (str.toLowerCase().includes(sequences.substring(i, i + 4))) return true;
    if (str.toLowerCase().includes(reversed.substring(i, i + 4))) return true;
  }
  return false;
}

/** 检测是否包含连续重复字符（如 aaaa、1111） */
export function isRepeating(str) {
  return /(.)\1{3,}/.test(str);
}

/**
 * 分析密码强度，返回 { score, categories, label, color }
 * @param {string} pwd
 * @returns {{ score: number, categories: number, label: string, color: string }}
 */
export function analyzePassword(pwd) {
  if (!pwd) return { score: 0, categories: 0, label: "无", color: "#b2bec3" };
  let categories = 0;
  if (/[a-z]/.test(pwd)) categories++;
  if (/[A-Z]/.test(pwd)) categories++;
  if (/\d/.test(pwd)) categories++;
  if (/[^a-zA-Z0-9]/.test(pwd)) categories++;
  let score = 0;
  score += Math.min(pwd.length * 4, 40);
  score += (categories - 1) * 15;
  if (pwd.length >= 12) score += 15;
  if (pwd.length >= 16) score += 10;
  score = Math.min(score, 100);
  let label, color;
  if (score < 30 || categories < 2) { label = "弱"; color = "#d63031"; }
  else if (score < 60 || categories < 3) { label = "中"; color = "#f39c12"; }
  else { label = "强"; color = "#00b894"; }
  return { score, categories, label, color };
}

/**
 * antd 表单校验器：新密码
 * 规则：>=8位，四类字符，禁止弱密码/连续/重复/纯数字/纯字母
 */
export function validatePassword(rule, value, callback) {
  if (!value) { callback("请输入新密码"); return; }
  if (value.length < 8) { callback("密码长度不能少于8位"); return; }
  const lower = value.toLowerCase();
  if (WEAK_PASSWORDS.some((w) => lower === w || lower.includes(w))) {
    callback("该密码为常见弱密码，请设置更复杂的密码"); return;
  }
  if (isSequential(value)) { callback("密码包含连续字符序列（如abcd、1234），请避免使用"); return; }
  if (isRepeating(value)) { callback("密码包含连续重复字符（如aaaa），请避免使用"); return; }
  if (/^\d+$/.test(value)) { callback("密码不能为纯数字"); return; }
  if (/^[a-zA-Z]+$/.test(value)) { callback("密码不能为纯字母"); return; }
  const info = analyzePassword(value);
  console.log("密码分析结果：", info);
  if (info.categories < 4) { callback("密码需同时包含大写字母、小写字母、数字和特殊符号4种字符类型"); return; }
  callback();
}

/**
 * 创建确认密码校验器
 * @param {object} formInstance - antd form 实例（this.props.form）
 * @param {string} matchFieldName - 需要匹配的字段名，默认 "newPassword"
 * @returns {function} antd validator
 */
export function createConfirmValidator(formInstance, matchFieldName = "newPassword") {
  return (rule, value, callback) => {
    if (!value) { callback("请再次输入密码"); return; }
    if (value !== formInstance.getFieldValue(matchFieldName)) {
      callback("两次输入的密码不一致");
    } else {
      callback();
    }
  };
}



async function getEncryptKey() {
    const res = await Request.GET(`${config.host}/encrypt-key/get`);
    if (res.success && res.data) {
      const { key, keyId, expiresIn } = res.data;
      const expiresAt = Date.now() + (parseInt(expiresIn, 10) || 300) * 1000;
      Storage.set('sm4_encrypt_key', { key, keyId, expiresAt });
      return { key, keyId };
    }
    return { key: null, keyId: null };
  }

function  normalizeKeyForSmCrypto(rawKey) {
    // 1. 使用 UTF-8 编码将原始字符串转换为字节数组
    const encoder = new TextEncoder();
    const rawKeyBytes = encoder.encode(rawKey);
    
    // 2. 创建一个长度为 16 的字节数组，并用 0 填充（模拟 Java 补 \0）
    const finalKey = new Uint8Array(16);
    
    // 3. 将原始密钥的字节放入新数组，如果长度不足 16，末尾会自动补 0
    const copyLength = Math.min(rawKeyBytes.length, 16);
    finalKey.set(rawKeyBytes.slice(0, copyLength));
    
    return finalKey;
  }


export async function encryptPassword(password){
  const { key: rawKey, keyId } = await getEncryptKey();
  const sm4Key = normalizeKeyForSmCrypto(rawKey);
  return {password: sm4.encrypt(password, sm4Key), keyId};
}

export async function handleChangePassword(params) {

  const newPassword = await encryptPassword(params['password']);
  return Request.POST(`${config.host}/account/password/reset?id=${params['id']}&newPassword=${newPassword.password}&keyId=${newPassword.keyId}`)
}


// WEBPACK FOOTER //
// ./src/util/passwordValidator.js